/**
 * WRITE-time richtext sanitizer (builder side, build-plan §6.4 / §10.3). Runs in Tiptap's `onUpdate`
 * BEFORE the HTML is written into `RichtextValue.html`, so the stored blob is already clean. Uses the
 * browser `dompurify` (the editor always has a DOM) fed the SAME `RICHTEXT_ALLOW` policy exported from
 * ui-react that the render-time `sanitizeRichtextHtml` uses — one source of truth, can never drift.
 *
 * Hooks:
 *  - `uponSanitizeAttribute` enforces the policy's `ALLOWED_URI_REGEXP` ONLY on `href` (http/https/
 *    mailto/relative; rejects javascript:/data:). We do NOT pass `ALLOWED_URI_REGEXP` to DOMPurify
 *    directly because that version applies it to every allowed attr (it would nuke `target`/`rel`).
 *    DOMPurify's secure default already blocks the dangerous schemes; this hook tightens it to ours.
 *  - `afterSanitizeAttributes` forces `rel="noopener noreferrer"` on any `target=_blank` link
 *    (reverse-tabnabbing defense).
 */
import DOMPurify from 'dompurify';
import { RICHTEXT_ALLOW, sanitizeStyleAttribute, type RichtextValue } from '@aquilixshop/ui-react';

let hooksInstalled = false;
function ensureHooks() {
  if (hooksInstalled) return;
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    // Both link `href` and image `src` are held to the URL allowlist (http(s)/root-relative only).
    if (
      (data.attrName === 'href' || data.attrName === 'src') &&
      !RICHTEXT_ALLOW.ALLOWED_URI_REGEXP.test(data.attrValue)
    ) {
      data.keepAttr = false;
    }
    // Same style filter as the render-time barrier: only the policy's four CSS properties survive, and
    // an attribute left with nothing is dropped rather than kept empty.
    if (data.attrName === 'style') {
      const clean = sanitizeStyleAttribute(data.attrValue);
      if (clean) data.attrValue = clean;
      else data.keepAttr = false;
    }
  });
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  hooksInstalled = true;
}

export function sanitizeRichtextWrite(html: string): string {
  ensureHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...RICHTEXT_ALLOW.ALLOWED_TAGS],
    ALLOWED_ATTR: [...RICHTEXT_ALLOW.ALLOWED_ATTR],
  });
}

/** Sanitize raw editor HTML into the tagged RichtextValue shape persisted in settings. */
export function toRichtext(html: string): RichtextValue {
  return { format: 'html', html: sanitizeRichtextWrite(html) };
}
